import {
  parseSquareBootstrap,
  squareProductsToEntries,
  squareProductsUrl,
  type SquareEntry,
  type SquareProduct,
  type SquareStore,
} from './SquareOnlineScraper';
import { wooHost } from './storeDocIds';
import {
  WOO_PAGE_SIZE,
  wooProductToEntry,
  wooProductsToEntries,
  type WooEntry,
  type WooProduct,
} from './WooCommerceScraper';

/**
 * The manual "paste the JSON" fallback for the store importers, for shops that
 * refuse every automated request (the WooCommerce escalation ladder ends in
 * StoreApiBlockedError) — the JSON counterpart of PasteHtmlModal's "paste the
 * page source" for generic URL sources. The curator opens the API URL in their
 * own browser, copies the response, and pastes it; it is validated and mapped
 * with the same functions the automatic fetch uses, so the import that follows
 * is identical.
 *
 * Pure — no Firebase, no DOM — so every shape is tested.
 */

// ── WooCommerce ──────────────────────────────────────────────────────────────

/**
 * The Store API URL to open by hand for a WooCommerce source URL. When the API
 * is blocked its category list cannot be read either, so a category URL is
 * addressed by its slug (the last path segment — the Store API's `category`
 * filter accepts slugs as well as ids) instead of the resolved id.
 */
export function wooPasteUrl(sourceUrl: string, page = 1): string | null {
  let u: URL;
  try {
    u = new URL(sourceUrl.trim());
  } catch {
    return null;
  }
  const segments = u.pathname.split('/').filter(Boolean);
  const catAt = segments.indexOf('product-category');
  const slug = catAt !== -1 ? segments[segments.length - 1] : undefined;
  const params = new URLSearchParams({
    per_page: String(WOO_PAGE_SIZE),
    page: String(page),
    ...(slug && slug !== 'product-category' ? { category: slug } : {}),
  });
  return `${u.origin}/wp-json/wc/store/v1/products?${params}`;
}

export type PasteParse<T> = { ok: true; value: T } | { ok: false; error: string };

function parseJsonText(text: string): PasteParse<unknown> {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, error: 'Nothing pasted.' };
  if (trimmed.startsWith('<')) {
    return {
      ok: false,
      error:
        'This is a web page, not the JSON response. Open the API address shown above itself (not the shop page) and copy what it shows.',
    };
  }
  try {
    return { ok: true, value: JSON.parse(trimmed) };
  } catch {
    return {
      ok: false,
      error: 'This is not valid JSON — make sure the whole response was copied (Cmd+A, Cmd+C).',
    };
  }
}

function isWooProduct(p: unknown): p is WooProduct {
  const r = p as Record<string, unknown> | null;
  return (
    !!r &&
    typeof r.id === 'number' &&
    typeof r.name === 'string' &&
    Array.isArray(r.images) &&
    !!r.prices &&
    typeof r.prices === 'object'
  );
}

export interface PastedWooPage {
  /** This page mapped without variant expansion (one entry per product). */
  entries: WooEntry[];
  /** The page's products (not variations), for mapping with expansion. */
  products: WooProduct[];
  /** The page's variations (a `type=variation` page), grouped later by parent. */
  variations: WooProduct[];
  /** A full page (100) — there may be another page to paste. */
  maybeMore: boolean;
}

/** The variations page to open by hand (Phase 2 variants; the whole shop's). */
export function wooVariationsPasteUrl(sourceUrl: string, page = 1): string | null {
  let u: URL;
  try {
    u = new URL(sourceUrl.trim());
  } catch {
    return null;
  }
  return `${u.origin}/wp-json/wc/store/v1/products?${new URLSearchParams({
    type: 'variation',
    per_page: String(WOO_PAGE_SIZE),
    page: String(page),
  })}`;
}

/** Pasted products + variations → entries, expanding variable products when asked. */
export function pastedWooToEntries(
  products: WooProduct[],
  variations: WooProduct[],
  host: string,
  expand: boolean,
): WooEntry[] {
  if (!expand) return products.map((p) => wooProductToEntry(p, host));
  const byParent = new Map<number, WooProduct[]>();
  for (const v of variations) {
    if (typeof v.parent === 'number') byParent.set(v.parent, [...(byParent.get(v.parent) ?? []), v]);
  }
  return wooProductsToEntries(products, host, byParent);
}

/** Variable products that still need their variations pasted (expansion on). */
export function variableProductsMissingVariations(products: WooProduct[], variations: WooProduct[]): number {
  const parents = new Set(variations.map((v) => v.parent));
  return products.filter((p) => p.type === 'variable' && (p.variations?.length ?? 0) > 1 && !parents.has(p.id)).length;
}

/** One pasted page of `/wp-json/wc/store/v1/products`, validated and mapped. */
export function parsePastedWooProducts(text: string, sourceUrl: string): PasteParse<PastedWooPage> {
  const json = parseJsonText(text);
  if (!json.ok) return json;
  const body = json.value as Record<string, unknown> | unknown[];
  if (!Array.isArray(body)) {
    if (body && typeof body === 'object' && Array.isArray((body as { products?: unknown }).products)) {
      return { ok: false, error: 'This looks like a Shopify products.json, not a WooCommerce Store API response.' };
    }
    if (body && typeof body === 'object' && 'code' in body) {
      return {
        ok: false,
        error: `The shop answered with an error (${String((body as { code: unknown }).code)}) — is this the right site, and the products address shown above?`,
      };
    }
    return { ok: false, error: 'Expected a list of products (a JSON array) from the WooCommerce Store API.' };
  }
  if (body.length === 0) return { ok: false, error: 'The response is an empty list — no products on this page.' };
  const bad = body.findIndex((p) => !isWooProduct(p));
  if (bad !== -1) {
    return {
      ok: false,
      error: `Item ${bad + 1} is not a WooCommerce product (needs id, name, images and prices) — wrong endpoint?`,
    };
  }
  let host: string;
  try {
    host = wooHost(sourceUrl);
  } catch {
    return { ok: false, error: 'The source URL is not valid.' };
  }
  const all = (body as WooProduct[]).filter((p) => !p.is_password_protected);
  const variations = all.filter((p) => p.type === 'variation');
  const products = all.filter((p) => p.type !== 'variation');
  return {
    ok: true,
    value: {
      entries: products.map((p) => wooProductToEntry(p, host)),
      products,
      variations,
      maybeMore: body.length >= WOO_PAGE_SIZE,
    },
  };
}

// ── Square Online ────────────────────────────────────────────────────────────

export type PastedSquare =
  /** The storefront page source — the ids, from which products are fetched automatically. */
  | { kind: 'storefront'; store: SquareStore }
  /** One page of the catalogue API's products response. */
  | {
      kind: 'products';
      entries: SquareEntry[];
      siteId: string;
      currentPage: number;
      totalPages: number;
    };

function isSquareProduct(p: unknown): p is SquareProduct & { site_id: string } {
  const r = p as Record<string, unknown> | null;
  return !!r && typeof r.id === 'string' && typeof r.name === 'string' && typeof r.site_id === 'string';
}

/**
 * A Square Online paste: either the storefront page source (contains the
 * `__BOOTSTRAP_STATE__` blob — the usual fix, since the storefront page is what
 * our server could not fetch) or one page of the products API JSON.
 */
export function parsePastedSquare(text: string, expandVariants = true): PasteParse<PastedSquare> {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, error: 'Nothing pasted.' };
  if (trimmed.startsWith('<') || trimmed.includes('__BOOTSTRAP_STATE__')) {
    const store = parseSquareBootstrap(trimmed);
    if (!store) {
      return {
        ok: false,
        error: 'This page source has no Square Online store data — is it the storefront page, fully copied (view source → Cmd+A)?',
      };
    }
    return { ok: true, value: { kind: 'storefront', store } };
  }
  const json = parseJsonText(trimmed);
  if (!json.ok) return json;
  const body = json.value as { data?: unknown; meta?: { pagination?: { current_page?: number; total_pages?: number } } };
  if (!body || typeof body !== 'object' || !Array.isArray(body.data)) {
    return {
      ok: false,
      error: 'Expected the storefront page source, or a Square Online products response ({ "data": [...] }).',
    };
  }
  if (body.data.length === 0) return { ok: false, error: 'The response has no products on this page.' };
  const bad = body.data.findIndex((p) => !isSquareProduct(p));
  if (bad !== -1) {
    return { ok: false, error: `Item ${bad + 1} is not a Square Online product (needs id, name, site_id) — wrong endpoint?` };
  }
  const products = (body.data as Array<SquareProduct & { site_id: string; visibility?: string }>).filter(
    (p) => p.visibility !== 'hidden',
  );
  const siteId = products[0]?.site_id ?? (body.data[0] as { site_id: string }).site_id;
  return {
    ok: true,
    value: {
      kind: 'products',
      entries: squareProductsToEntries(products, siteId, expandVariants),
      siteId,
      currentPage: body.meta?.pagination?.current_page ?? 1,
      totalPages: body.meta?.pagination?.total_pages ?? 1,
    },
  };
}

/** The products API page URL to open by hand, once the store ids are known. */
export function squarePasteUrl(store: Pick<SquareStore, 'userId' | 'siteId'>, categoryId: string | undefined, page: number): string {
  return squareProductsUrl(store, categoryId, page);
}

/** Merge pasted pages, de-duplicating by doc identity (re-pasting a page is harmless). */
export function mergePastedEntries<T extends { wooProductId?: string; squareProductId?: string }>(
  existing: T[],
  next: T[],
): T[] {
  const key = (e: T) => e.wooProductId ?? e.squareProductId ?? '';
  const seen = new Set(existing.map(key));
  return [...existing, ...next.filter((e) => !seen.has(key(e)))];
}
